import { callFunction } from "./functions-client";

// User teams / organizations with granular RBAC.
export const ORG_ROLES = ["owner", "admin", "manager", "member", "viewer"] as const;
export const ORG_ROLE_LABELS: Record<string, string> = {
  owner: "Owner", admin: "Admin", manager: "Manager", member: "Member", viewer: "Viewer",
};
// Roles that can be assigned via invite / role editor (owner is implicit).
export const ASSIGNABLE_ORG_ROLES = ["admin", "manager", "member", "viewer"] as const;

export interface Org { id: string; name: string; created_at: string; is_owner: boolean; roles: string[] }
export interface OrgMember { account_id: string; email: string | null; status: string; joined_at: string; roles: string[] }
export interface OrgInvite { id: string; email: string; role: string; expires_at: string; created_at: string }

export const listMyOrgs = () => callFunction<{ organizations: Org[] }>("orgs/list-mine", { method: "GET" });
export const createOrg = (name: string) => callFunction<{ org_id: string }>("orgs/create", { body: { name } });
export const getOrg = (id: string) =>
  callFunction<{ org: Org; my_roles: string[]; my_permissions: string[] }>(`orgs/get?id=${encodeURIComponent(id)}`, { method: "GET" });
export const getMembers = (id: string) =>
  callFunction<{ members: OrgMember[]; invites: OrgInvite[]; owner_account_id: string | null }>(`orgs/members?id=${encodeURIComponent(id)}`, { method: "GET" });
export const inviteMember = (org_id: string, email: string, role: string) =>
  callFunction<{ ok: boolean; invite_token: string }>("orgs/invite", { body: { org_id, email, role } });
export const acceptInvite = (token: string) =>
  callFunction<{ ok: boolean; org_id: string }>("orgs/accept-invite", { body: { token } });
export const setMemberRoles = (org_id: string, account_id: string, roles: string[]) =>
  callFunction<{ ok: boolean }>("orgs/set-member-roles", { body: { org_id, account_id, roles } });
export const removeMember = (org_id: string, account_id: string) =>
  callFunction<{ ok: boolean }>("orgs/remove-member", { body: { org_id, account_id } });
export const leaveOrg = (org_id: string) => callFunction<{ ok: boolean }>("orgs/leave", { body: { org_id } });

// Client mirror of the org permission catalog (UI gating; server re-checks).
export const ORG_PERMISSIONS: Record<string, readonly string[]> = {
  "org.manage_members": ["owner", "admin"],
  "org.manage_roles": ["owner", "admin"],
  "jobs.post": ["owner", "admin", "manager", "member"],
  "loads.post": ["owner", "admin", "manager", "member"],
  "analytics.view": ["owner", "admin", "manager", "viewer"],
};
export function orgCan(roles: string[], perm: string): boolean {
  return (ORG_PERMISSIONS[perm] ?? []).some((r) => roles.includes(r));
}
